import React from "react";
import {
  ActivityIndicator,
  ImageBackground,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { sign_bg } from "../../../constants/icons";
import { SignInViewProps } from "../auth.types";
import LogoSquare from "../../../assets/logoSquare";
import PasswordInput from "./PasswordInput";

export default function SignInView({
  email,
  password,
  error,
  isSigningIn,
  canSignIn,
  onEmailChange,
  onPasswordChange,
  onSubmitSignIn,
  onSwitchToSignUp,
  onSwitchToForgotPassword,
}: SignInViewProps) {
  return (
    <KeyboardAvoidingView
      style={styles.root}
      behavior={Platform.OS === "ios" ? "padding" : undefined}
    >
      <ImageBackground source={sign_bg} style={styles.bg} resizeMode="cover">
        {/* Top-left logo */}
        <View style={styles.topBar}>
          <LogoSquare width={119} height={148} />
        </View>

        {/* Center card */}
        <View style={styles.centerWrap}>
          <View style={styles.card}>
            <View style={styles.header}>
              <Text style={styles.title}>
                You&apos;re in a safe place to practice meaningful
                conversations.
              </Text>

              <Text style={styles.subtitle}>
                In KNEXO, conversations are guided, time-limited, and designed
                to feel respectful and comfortable.
              </Text>
            </View>

            {/* EMAIL */}
            <TextInput
              style={styles.input}
              placeholder="Email"
              placeholderTextColor="#8B9099"
              value={email}
              onChangeText={onEmailChange}
              autoCapitalize="none"
              keyboardType="email-address"
              returnKeyType="next"
              testID="signin-email-input"
            />

            {/* PASSWORD */}
            <PasswordInput
              style={[styles.input, styles.inputMutedBorder]}
              placeholder="Password"
              placeholderTextColor="#8B9099"
              value={password}
              onChangeText={onPasswordChange}
              returnKeyType="done"
              onSubmitEditing={onSubmitSignIn}
              testID="signin-password-input"
            />

            {error ? (
              <Text style={styles.errorText} testID="signin-error">
                {error}
              </Text>
            ) : null}

            {/* Forgot Password */}
            {onSwitchToForgotPassword && (
              <TouchableOpacity
                onPress={onSwitchToForgotPassword}
                style={styles.forgotPasswordButton}
                testID="forgot-password-button"
              >
                <Text style={styles.forgotPasswordText}>Forgot Password</Text>
              </TouchableOpacity>
            )}

            {/* Submit */}
            <TouchableOpacity
              onPress={onSubmitSignIn}
              disabled={!canSignIn}
              style={[styles.btn, !canSignIn && styles.btnDisabled]}
              testID="signin-button"
            >
              {isSigningIn ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text style={styles.btnText}>Enter KNEXO</Text>
              )}
            </TouchableOpacity>

            {/* Switch to Sign Up */}
            {onSwitchToSignUp && (
              <TouchableOpacity
                onPress={onSwitchToSignUp}
                style={styles.switchButton}
                testID="switch-to-signup-button"
              >
                <Text style={styles.switchText}>
                  New here?{" "}
                  <Text style={styles.switchTextBold}>
                    Create a free account?
                  </Text>
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </ImageBackground>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    // чтобы не было белых краёв даже пока фон грузится
    backgroundColor: "#fff",
  },

  bg: {
    flex: 1,
    width: "100%",
    height: "100%",
  },

  topBar: {
    paddingTop: 24,
    paddingHorizontal: 24,
  },

  centerWrap: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 24, // отступ только для контента, фон остаётся без отступов
    paddingBottom: 24,
  },

  card: {
    width: "100%",
    maxWidth: 587,
    minHeight: 661,
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#D8DBE1",
    paddingHorizontal: 48,
    paddingVertical: 44,

    shadowColor: "#000",
    shadowOpacity: 0.05,
    shadowRadius: 8,
    shadowOffset: { width: 8, height: 8 },
    elevation: 4,
    marginBottom: 80,
  },

  header: {
    width: "100%",
    marginBottom: 18,
  },

  title: {
    fontFamily: "LexendBold",
    fontSize: 36,
    lineHeight: 40,
    fontWeight: "700",
    color: "#2B2B2F",
  },

  subtitle: {
    marginTop: 10,
    fontFamily: "Lexend",
    fontSize: 16,
    lineHeight: 22,
    fontWeight: "400",
    color: "#2B2B2F",
  },

  input: {
    width: "100%",
    height: 56,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#7EADEB",
    backgroundColor: "#FFFFFF",
    paddingHorizontal: 18,
    fontSize: 16,
    color: "#2B2B2F",
    marginTop: 14,
    fontFamily: "Lexend",
  },

  inputMutedBorder: {
    borderColor: "#D8DBE1",
  },

  forgotPasswordButton: {
    alignSelf: "center",
    marginTop: 14,
    paddingVertical: 6,
    paddingHorizontal: 10,
  },
  forgotPasswordText: {
    fontFamily: "LexendBold",
    fontSize: 12,
    fontWeight: "500",
    color: "#3A6FB1",
  },

  btn: {
    width: "100%",
    height: 56,
    borderRadius: 16,
    backgroundColor: "#3A6FB1",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 16,
  },
  btnDisabled: { opacity: 0.55 },
  btnText: {
    fontFamily: "Lexend",
    color: "#FFFFFF",
    fontWeight: "600",
    fontSize: 16,
  },

  switchButton: {
    marginTop: 18,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 8,
  },
  switchText: {
    fontFamily: "Lexend",
    fontSize: 12,
    color: "#2B2B2F",
    textAlign: "center",
  },
  switchTextBold: {
    fontFamily: "Lexend",
    fontWeight: "600",
    color: "#3A6FB1",
  },

  errorText: {
    marginTop: 10,
    color: "#D32F2F",
    fontSize: 13,
    fontFamily: "Lexend",
  },
});
